import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { IMAGES } from "./data";
import { MaskedLines, EASE } from "./Reveal";
import { useSmoothScroll } from "../../App";

export default function Hero() {
  const scrollTo = useSmoothScroll();
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, reduce ? 0 : 160]);
  const bgScale = useTransform(scrollY, [0, 900], [1, reduce ? 1 : 1.1]);

  return (
    <section id="top" data-testid="hero-section" className="relative flex min-h-[100svh] items-center overflow-hidden bg-forest">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <motion.img
          src={IMAGES.hero}
          alt="A Bengal tiger moving through the forests of Karnataka"
          initial={{ scale: reduce ? 1 : 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div
        className="absolute inset-0 bg-gradient-to-r from-forest/95 via-forest/60 to-forest/25"
        aria-hidden="true"
      />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-forest to-transparent" aria-hidden="true" />

      <div className="relative z-10 mx-auto w-full max-w-editorial px-6 pb-28 pt-36 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-px w-10 bg-saffron" aria-hidden="true" />
          <p data-testid="hero-eyebrow" className="text-[11px] font-medium uppercase tracking-[0.34em] text-saffron">
            A wildlife photography & slow travel journey
          </p>
        </motion.div>

        <h1 className="max-w-4xl font-serif font-light leading-[1.02] text-ivory text-[clamp(3rem,8vw,7rem)]">
          <MaskedLines as="span" delay={0.55} lines={["Where the Forest"]} lineClassName="block" />
          <MaskedLines
            as="span"
            delay={0.7}
            lines={["Teaches You to Slow Down"]}
            lineClassName="block italic text-saffron"
          />
        </h1>

        <motion.p
          data-testid="hero-subtext"
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
          className="mt-8 max-w-xl font-serif text-lg italic leading-relaxed text-ivory/85 md:text-xl"
        >
          12 Days through Kabini&rsquo;s big-cat country, Wayanad&rsquo;s misted spice hills, the Malabar coast and
          the slow water of the Kerala backwaters — crafted by Meet The Gods Ecotrails.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.4, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-explore-button"
            onClick={() => scrollTo("#itinerary")}
            className="bg-saffron px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.24em] text-forest transition-colors duration-300 hover:bg-ivory"
          >
            Explore Itinerary
          </button>
          <button
            data-testid="hero-quote-button"
            onClick={() => scrollTo("#enquire")}
            className="border border-ivory/60 px-8 py-4 text-[11px] font-medium uppercase tracking-[0.24em] text-ivory transition-colors duration-300 hover:border-saffron hover:text-saffron"
          >
            Request a Quote
          </button>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-button"
        onClick={() => scrollTo("#journey")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-center text-[10px] font-medium uppercase tracking-[0.34em] text-ivory/60 transition-colors hover:text-saffron"
      >
        Scroll
        <ChevronDown className="mx-auto mt-2 h-4 w-4 animate-bounce" />
      </motion.button>
    </section>
  );
}
